import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useUserAuthOptional } from '../contexts/UserAuthContext';
import { getPendingSave, clearPendingSave } from '../utils/pendingSave';

const LEGACY_STORAGE_KEY = 'fieravicino_saved_events';

interface UseUserFavoritesSyncOptions {
  /**
   * Replace the in-memory favorites set with the merged authoritative DB set.
   * Called by the hook after merging localStorage legacy IDs and DB rows.
   */
  setSavedIds: (ids: Set<string>) => void;
}

/**
 * Synchronize favorites between localStorage (legacy pre-Opzione B) and the
 * authoritative `user_favorites` table whenever an authenticated session is
 * established. Designed to be called from inside SavedEventsProvider.
 *
 * Behavior:
 *  - On user.id change (authenticated): merge legacy localStorage IDs with DB
 *    rows, upsert any local-only IDs, clear localStorage, then apply any
 *    pendingSave intent (T021) and navigate back to the original page.
 *  - On SIGNED_OUT (user becomes null): reset the in-memory set and the
 *    idempotency ref (so re-login triggers a fresh sync).
 *
 * Idempotency: tracks the last user.id synced via useRef. Skips re-sync on
 * re-renders for the same user.
 */
export function useUserFavoritesSync({ setSavedIds }: UseUserFavoritesSyncOptions): void {
  const { user } = useUserAuthOptional();
  const lastSyncedRef = useRef<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // SIGNED_OUT — reset cache and ref
    if (!user) {
      lastSyncedRef.current = null;
      setSavedIds(new Set());
      return;
    }

    // Idempotency guard
    if (lastSyncedRef.current === user.id) return;

    let cancelled = false;
    const userId = user.id;

    (async () => {
      try {
        // 1. Read legacy localStorage IDs
        let legacyIds: string[] = [];
        try {
          const raw = window.localStorage.getItem(LEGACY_STORAGE_KEY);
          if (raw) {
            const parsed: unknown = JSON.parse(raw);
            if (Array.isArray(parsed)) {
              legacyIds = parsed.filter((x): x is string => typeof x === 'string');
            }
          }
        } catch {
          // malformed JSON — ignore
        }

        // 2. Fetch existing DB rows
        const { data: dbRows, error: selectError } = await supabase
          .from('user_favorites')
          .select('event_id')
          .eq('user_id', userId);
        if (selectError) {
          console.warn('[useUserFavoritesSync] select failed:', selectError.message);
          return;
        }
        if (cancelled) return;

        const dbIds = new Set<string>(
          (dbRows ?? [])
            .map((r) => (r as { event_id: unknown }).event_id)
            .filter((x): x is string => typeof x === 'string')
        );

        // 3. Merge — upsert legacy IDs not yet in DB (per-row, so FK violations
        //    on deleted events do not abort the whole batch).
        const toUpsert = legacyIds.filter((id) => !dbIds.has(id));
        const merged = new Set<string>(dbIds);
        for (const eventId of toUpsert) {
          try {
            const { error: upsertError } = await supabase
              .from('user_favorites')
              .upsert({ user_id: userId, event_id: eventId }, { onConflict: 'user_id,event_id' });
            if (upsertError) {
              console.warn(
                `[useUserFavoritesSync] upsert failed for event ${eventId}:`,
                upsertError.message
              );
              continue;
            }
            merged.add(eventId);
          } catch (err) {
            console.warn(`[useUserFavoritesSync] upsert threw for event ${eventId}:`, err);
          }
        }
        if (cancelled) return;

        // 4. Update in-memory state
        setSavedIds(merged);

        // 5. Opzione B: clear legacy cache (DB is now source of truth)
        try {
          window.localStorage.removeItem(LEGACY_STORAGE_KEY);
        } catch {
          // ignore
        }

        // 6. Mark as synced
        lastSyncedRef.current = userId;

        // 7. Apply pending save intent (T021) — runs only after the merge so the
        //    UI reflects the saved state without flicker.
        const pending = getPendingSave();
        if (pending && !merged.has(pending.eventId)) {
          try {
            const { error: pendingError } = await supabase
              .from('user_favorites')
              .insert({ user_id: userId, event_id: pending.eventId });
            if (pendingError) {
              console.warn(
                '[useUserFavoritesSync] pending save insert failed:',
                pendingError.message
              );
            } else if (!cancelled) {
              const next = new Set(merged);
              next.add(pending.eventId);
              setSavedIds(next);
            }
          } catch (err) {
            console.warn('[useUserFavoritesSync] pending save threw:', err);
          }
          clearPendingSave();
          if (!cancelled) {
            navigate(pending.returnUrl);
          }
        } else if (pending) {
          // Already saved — just clear and navigate back
          clearPendingSave();
          if (!cancelled) navigate(pending.returnUrl);
        }
      } catch (err) {
        console.warn('[useUserFavoritesSync] unexpected error:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
    // setSavedIds and navigate are stable; depending only on user.id keeps the
    // sync idempotent per session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);
}
